'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useApiMutation } from '@shared/api-utils';
import { createDebug } from '@shared/debug';
import { useClientConfig } from '@shared/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { createSkippableDepositLimitSchema, type DepositLimitTranslate, getDepositLimitPeriod } from '../../../../../DepositLimits/DepositLimitSchema';
import { DepositLimitsStep as RegistrationDepositLimitsStep } from '../../../../../Forms/Registration/steps/DepositLimitsStep';
import type { DepositLimitsResponse, PlayerLimit, PlayerLimitRequest } from '../../../../../my-account/SaferGambling/types';
import { createPlayerLimitRequest, getDepositLimitPath, getDepositLimitsPath } from '../../../../../my-account/SaferGambling/utils/playerLimits';
import { toast } from '../../../../../Toast/Toast';
import { useGlobalSheet } from '../../../../context/GlobalSheetContext';

// Form structure for deposit limits only (matches registration form structure)
type DepositLimitsFormData = {
  depositLimits: {
    period: {
      label: string;
      value: number;
    };
    amount: string;
  };
};

// Create a minimal form schema for deposit limits (matches registration schema)
const createDepositLimitsSchema = (t: DepositLimitTranslate, currentLimits?: DepositLimitsResponse | null) => {
  return z.object({
    depositLimits: createSkippableDepositLimitSchema(t).refine(
      data => {
        // Skip validation if no current limits or amount is -1 (do it later)
        if (!currentLimits || data.amount === -1) {
          return true;
        }

        const selectedPeriod = getDepositLimitPeriod(data.period.value);
        const currentLimit = currentLimits.limits?.find(limit => limit.limitType === 'DEPOSIT' && limit.period === selectedPeriod)?.limitValue;

        if (!currentLimit || currentLimit === 0) {
          return true;
        }

        const newAmountInCents = Math.round(data.amount * 100);

        const isCurrentLimitInBaseCurrency = data.amount === currentLimit;
        const currentLimitInCents = isCurrentLimitInBaseCurrency ? newAmountInCents : currentLimit;

        // Amount must be different from current limit
        return newAmountInCents !== currentLimitInCents;
      },
      {
        message: t('validation.depositLimit.unchanged'),
        path: ['amount'], // Attach error to the amount field
      }
    ),
  });
};

type DepositLimitsStepProps = {
  /**
   * Whether to show the "Do it later" button
   * @default false
   */
  showDoItLater?: boolean;
  /**
   * Optional callback to register a custom back handler
   * Used to register back navigation handler with the orchestrator
   */
  onRegisterBackHandler?: (handler: () => boolean) => void;
  depositLimits: DepositLimitsResponse | null;
};

/**
 * Wrapper component for DepositLimitsStep in the deposit flow.
 * Creates its own form context and uses the registration form component.
 */
function DepositLimitsStep({ showDoItLater = false, onRegisterBackHandler, depositLimits }: DepositLimitsStepProps) {
  const debug = createDebug({ isEnabled: true });
  const t = useTranslations('form');
  const depositSheetTranslations = useTranslations('global_sheets.deposit');
  const formSchema = createDepositLimitsSchema(t, depositLimits);
  const form = useForm<DepositLimitsFormData>({
    resolver: zodResolver(formSchema) as never,
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    defaultValues: {
      depositLimits: {
        period: { label: t('buttons.monthly'), value: 0 },
        amount: '',
      },
    },
  });
  const { updateDepositSheetTitle } = useGlobalSheet();

  useEffect(() => {
    updateDepositSheetTitle(depositSheetTranslations('deposit_limits.title'));
  }, [updateDepositSheetTitle, depositSheetTranslations]);

  const queryClient = useQueryClient();
  const { currency } = useClientConfig();

  const limitPeriodValue = form.watch('depositLimits.period.value');
  const limitPeriod = getDepositLimitPeriod(limitPeriodValue);
  const hasExistingLimit = Boolean(depositLimits?.limits?.find(limit => limit.period === limitPeriod && (limit.limitValue ?? 0) > 0));

  const { mutate: addDepositLimit } = useApiMutation<PlayerLimit, PlayerLimitRequest>(getDepositLimitPath(limitPeriod), hasExistingLimit ? 'PUT' : 'POST', {
    mutationOptions: {
      onError: (error: Error) => {
        debug.forceError('Error updating deposit limit:', error);
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ['api', getDepositLimitsPath()] });
      },
    },
    requestOptions: {
      credentials: true,
    },
  });

  const handleSetDepositLimit = async (): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      try {
        const data = form.getValues();
        const depositvalue = Number(data.depositLimits.amount);
        addDepositLimit(createPlayerLimitRequest(depositvalue, currency), {
          onSuccess: () => {
            toast(depositSheetTranslations('deposit_limits.updated_toast'), { type: 'success' });
            resolve();
          },
          onError: () => {
            toast(depositSheetTranslations('deposit_limits.unknown_error_toast'), { type: 'error' });
            reject(new Error('Failed to add deposit limit'));
          },
        });
      } catch (error) {
        debug.forceError('Validation error:', error);
        reject(error);
      }
    });
  };

  return (
    <FormProvider {...form}>
      <RegistrationDepositLimitsStep
        onSuccessStep="amount"
        onSuccessStatus=""
        onSuccessCallback={handleSetDepositLimit}
        depositLimits={depositLimits}
        validateWithApi={false}
        showDoItLater={showDoItLater}
        onBackStep="amount"
        onRegisterBackHandler={onRegisterBackHandler}
        showLogo={false}
        showBackButton={false}
        layoutClassName="p-0 md:p-0"
        dataTrackFeature="Deposit Limits"
        dataTrackContainer="Set Deposit Limit"
      />
    </FormProvider>
  );
}

export { DepositLimitsStep };
